import { DYNAMIC_TYPE_OPTIONS, PlanningState, initialPlanningState } from '../types/planning';

// Keep in sync with persistence key used in `appStore.ts`
const PLANNING_STORAGE_KEY = 'planningState';

export function savePlanningState(state: PlanningState): void {
  try {
    localStorage.setItem(PLANNING_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save planning state to localStorage:', error);
  }
}

export function loadPlanningState(): PlanningState {
  try {
    const saved = localStorage.getItem(PLANNING_STORAGE_KEY);
    if (saved) {
      return { ...initialPlanningState, ...JSON.parse(saved) };
    }
  } catch (error) {
    console.warn('Failed to load planning state from localStorage:', error);
  }
  return initialPlanningState;
}

export function clearPlanningState(): void {
  try {
    localStorage.removeItem(PLANNING_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear planning state from localStorage:', error);
  }
}

export function generateConceptSentence(state: PlanningState): string {
  const playerRole = state.playerRole || '[PLAYER ROLE]';
  const playerName = state.playerName || '[PLAYER NAME]';
  const playerMotivation = state.playerMotivation || "[PLAYER'S INITIAL MOTIVATION]";
  const npcRole = state.npcRole || '[NPC ROLE]';
  const npcName = state.npcName || '[NPC NAME]';
  const npcMotivation = state.npcMotivation || "[NPC'S CORE MOTIVATION]";
  const aPlot = state.aPlot || '[THE A-PLOT]';
  const stakes = state.stakes || '[STAKES]';
  const location = state.locationAtmosphere || '[LOCATION]';
  const bPlotSeed = state.bPlotSeed || '[B-PLOT SEED IDEA]';

  return `The ${playerRole} named ${playerName}, driven by ${playerMotivation}, and the ${npcRole} named ${npcName}, motivated by ${npcMotivation}, are talking about ${aPlot}. The stakes involve ${stakes}. This occurs in ${location}.${bPlotSeed ? ` The NPC might later try to divert the conversation by talking about ${bPlotSeed}.` : ''}`;
}

export interface ConceptPart {
  text: string;
  type:
    | 'static'
    | 'playerRole'
    | 'playerName'
    | 'playerMotivation'
    | 'npcRole'
    | 'npcName'
    | 'npcMotivation'
    | 'aPlot'
    | 'dynamicType'
    | 'stakes'
    | 'location'
    | 'bPlotSeed'
    | 'bPlotReason'
    | 'bPlotNature'
    | 'bPlotExplanation';
  isFilled: boolean;
}

export function isFieldCompleteByWords(text: string | undefined, minWords: number): boolean {
  if (!text) return false;
  const wordCount = text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
  return wordCount >= minWords;
}

function getLabelForReason(reason: string, reasonOther: string): string {
  const reasonMap: Record<string, string> = {
    intimidate: 'intimidate',
    intrigue: 'intrigue',
    evade: 'evade',
    show_value: 'show value',
    be_understood: 'be understood',
    find_common_ground: 'find common ground',
  };
  return reason === 'other' ? reasonOther : reasonMap[reason] || reason;
}

function getLabelForNature(nature: string, natureOther: string): string {
  const natureMap: Record<string, string> = {
    anecdote: 'an anecdote',
    philosophical_point: 'a philosophical point',
    parable: 'a parable',
    memory: 'a memory',
    personal_revelation: 'a personal revelation',
    option: 'an option',
  };
  return nature === 'other' ? natureOther : natureMap[nature] || nature;
}

export function generateConceptParts(
  state: PlanningState,
  includeSegment2Summary = false
): ConceptPart[] {
  const parts: ConceptPart[] = [
    { text: 'The ', type: 'static', isFilled: true },
    { text: state.playerRole || '[PLAYER ROLE]', type: 'playerRole', isFilled: !!state.playerRole },
    { text: ' named ', type: 'static', isFilled: true },
    { text: state.playerName || '[PLAYER NAME]', type: 'playerName', isFilled: !!state.playerName },
    { text: ', driven by ', type: 'static', isFilled: true },
    {
      text: state.playerMotivation || "[PLAYER'S INITIAL MOTIVATION]",
      type: 'playerMotivation',
      isFilled: !!state.playerMotivation,
    },
    { text: ', and the ', type: 'static', isFilled: true },
    { text: state.npcRole || '[NPC ROLE]', type: 'npcRole', isFilled: !!state.npcRole },
    { text: ' named ', type: 'static', isFilled: true },
    { text: state.npcName || '[NPC NAME]', type: 'npcName', isFilled: !!state.npcName },
    { text: ', motivated by ', type: 'static', isFilled: true },
    {
      text: state.npcMotivation || "[NPC'S CORE MOTIVATION]",
      type: 'npcMotivation',
      isFilled: !!state.npcMotivation,
    },
    { text: ', are talking about ', type: 'static', isFilled: true },
    { text: state.aPlot || '[THE A-PLOT]', type: 'aPlot', isFilled: !!state.aPlot },
    { text: '.', type: 'static', isFilled: true },
    { text: '\n\n', type: 'static', isFilled: true },
    {
      text: 'The game starts mid-way through a conversation between these two people while they are in ',
      type: 'static',
      isFilled: true,
    },
    {
      text: state.locationAtmosphere || '[LOCATION]',
      type: 'location',
      isFilled: !!state.locationAtmosphere,
    },
    { text: '. Their relationship dynamic is one of a ', type: 'static', isFilled: true },
    {
      text: `'${state.dynamicType ? DYNAMIC_TYPE_OPTIONS.find((opt) => opt.value === state.dynamicType)?.label || state.dynamicType : '[RELATIONSHIP DYNAMIC]'}'`,
      type: 'dynamicType',
      isFilled: !!state.dynamicType,
    },
    { text: ' situation, with stakes ultimately involving ', type: 'static', isFilled: true },
    { text: state.stakes || '[STAKES]', type: 'stakes', isFilled: !!state.stakes },
    { text: '.', type: 'static', isFilled: true },
  ];

  if (state.bPlotSeed) {
    parts.push(
      {
        text: ' The NPC might later try to divert the conversation by talking about ',
        type: 'static',
        isFilled: true,
      },
      { text: state.bPlotSeed, type: 'bPlotSeed', isFilled: true },
      { text: '.', type: 'static', isFilled: true }
    );
  }

  // Add B-Plot summary for segments 2+
  if (includeSegment2Summary) {
    const npcName = state.npcName || '[NPC NAME]';
    const reasonText = state.bPlotReason
      ? getLabelForReason(state.bPlotReason, state.bPlotReasonOther)
      : '[carry out their motivation]';
    const natureText = state.bPlotNature
      ? getLabelForNature(state.bPlotNature, state.bPlotNatureOther)
      : '[type of diversion]';

    parts.push(
      { text: '\n\n', type: 'static', isFilled: true },
      { text: npcName, type: 'npcName', isFilled: !!state.npcName },
      { text: ' diverts the conversation via ', type: 'static', isFilled: true },
      { text: natureText, type: 'bPlotNature', isFilled: !!state.bPlotNature },
      { text: ' in order to ', type: 'static', isFilled: true },
      { text: reasonText, type: 'bPlotReason', isFilled: !!state.bPlotReason },
      { text: ' -- ', type: 'static', isFilled: true },
      { text: natureText, type: 'bPlotNature', isFilled: !!state.bPlotNature },
      { text: ' being their chosen form of diversion because of ', type: 'static', isFilled: true },
      {
        text: state.bPlotExplanation || '[EXPLANATION]',
        type: 'bPlotExplanation',
        isFilled: !!state.bPlotExplanation,
      },
      { text: '.', type: 'static', isFilled: true }
    );
  }

  return parts;
}
